import React from 'react';
import { Box, Button, Container, Stack, Typography } from '@mui/material';
import { FiAlertOctagon, FiRefreshCw } from 'react-icons/fi';

/**
 * App-level error boundary. Catches render errors anywhere below in the tree
 * and shows a recoverable fallback UI instead of a white screen.
 *
 * - Reports to Sentry if available on window (set by utilities/sentry.js).
 * - "Try again" resets local state.
 * - "Reload" forces a full page reload, which also re-fetches lazy chunks
 *   that may have failed due to a stale deploy (common cause of ChunkLoadError).
 */
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, info) {
        // Best-effort Sentry capture without taking a hard dependency.
        const Sentry = typeof window !== 'undefined' ? window.Sentry : null;
        if (Sentry && typeof Sentry.captureException === 'function') {
            Sentry.captureException(error, { extra: info });
        }
        if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.error('[ErrorBoundary]', error, info);
        }

        // Chunk load failure after a new deploy → force reload once.
        const isChunkErr = error && /ChunkLoadError|Loading chunk|Loading CSS chunk/i.test(error.message || '');
        if (isChunkErr && typeof window !== 'undefined' && !sessionStorage.getItem('lpc-chunk-reload')) {
            sessionStorage.setItem('lpc-chunk-reload', '1');
            window.location.reload();
        }
    }

    handleReset = () => this.setState({ hasError: false, error: null });

    handleReload = () => {
        if (typeof window !== 'undefined') window.location.reload();
    };

    render() {
        if (!this.state.hasError) return this.props.children;

        return (
            <Box sx={{ minHeight: '70vh', display: 'flex', alignItems: 'center', py: 6 }}>
                <Container maxWidth="sm">
                    <Stack spacing={3} alignItems="center" textAlign="center">
                        <Box sx={{ color: 'error.main', fontSize: 56, display: 'inline-flex' }}>
                            <FiAlertOctagon />
                        </Box>
                        <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
                            Something went wrong
                        </Typography>
                        <Typography sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                            We hit an unexpected error. Try again, or reload the page to recover.
                            If this keeps happening, please contact us.
                        </Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <Button variant="contained" onClick={this.handleReset} startIcon={<FiRefreshCw />}>
                                Try again
                            </Button>
                            <Button variant="outlined" onClick={this.handleReload}>
                                Reload page
                            </Button>
                        </Stack>
                    </Stack>
                </Container>
            </Box>
        );
    }
}

export default ErrorBoundary;
